import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';
import { modeNev } from '../../mode-nevek';

/**
 *  ADMIN → RENDSZER → HIBAJEGY – REDMINE KAPCSOLAT.
 *
 *  A felhasználók hibabejelentései ide, ebbe a Redmine-projektbe mennek, és innen jönnek
 *  vissza az állapotváltások és a nyilvános megjegyzések (backend/hibajegy.js).
 *
 *  Az API-KULCS CSAK BEÍRHATÓ: a kiszolgáló soha nem küldi vissza, csak azt, hogy be van-e
 *  állítva. Üres mezővel mentve a régi kulcs marad – ugyanaz a szabály, mint a levelezési
 *  jelszónál.
 *
 *  A funkció alrendszerenként kapcsolható (a lista a telepítésé, nem az intézményé); ma még
 *  csak a SULINFORM-nak van menüpontja, ezért csak az választható.
 */
@Component({
  selector: 'app-admin-hibajegy-beallitas',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './hibajegy-beallitas.component.html',
  styleUrls: ['./hibajegy-beallitas.component.scss'],
})
export class AdminHibajegyBeallitasComponent implements OnInit {
  toltodik = true;
  ment = false;
  probaFut = false;
  szinkronFut = false;
  hiba = '';

  b: any = { redmine_url: '', projekt: '', tracker_id: '', formatum: 'common_mark', alrendszerek: [] as string[] };
  kulcs = '';
  kulcsBeallitva = false;
  kulcsTorles = false;
  tamogatott: string[] = [];
  formatumok: string[] = [];
  statisztika: any = null;
  modositva: string | null = null;
  modosito: string | null = null;

  proba: any = null;
  szinkron: any = null;

  modeNev = modeNev;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() { await this.betolt(); }

  private atvesz(r: any) {
    this.b = {
      redmine_url: r?.redmine_url || '', projekt: r?.projekt || '',
      tracker_id: r?.tracker_id ?? '', formatum: r?.formatum || 'common_mark',
      alrendszerek: Array.isArray(r?.alrendszerek) ? [...r.alrendszerek] : [],
    };
    this.kulcsBeallitva = !!r?.api_kulcs_beallitva;
    this.tamogatott = r?.tamogatott_alrendszerek || [];
    this.formatumok = r?.formatumok || ['common_mark', 'textile'];
    this.statisztika = r?.statisztika || null;
    this.modositva = r?.modositva || null;
    this.modosito = r?.modosito || null;
  }

  async betolt() {
    this.toltodik = true;
    this.hiba = '';
    try {
      this.atvesz(await firstValueFrom(this.http.get('/api/hibajegy/beallitas')));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A beállítás nem tölthető be.';
    } finally {
      this.toltodik = false;
    }
  }

  bekapcsolva(a: string): boolean { return this.b.alrendszerek.includes(a); }

  alrendszerValt(a: string, be: boolean) {
    const halmaz = new Set<string>(this.b.alrendszerek);
    if (be) halmaz.add(a); else halmaz.delete(a);
    this.b.alrendszerek = [...halmaz];
  }

  async mentes() {
    if (this.ment) return;
    this.ment = true;
    try {
      const r = await firstValueFrom(this.http.put('/api/hibajegy/beallitas', {
        ...this.b,
        tracker_id: this.b.tracker_id === '' || this.b.tracker_id === null ? null : Number(this.b.tracker_id),
        api_kulcs: this.kulcs || undefined,
        api_kulcs_torles: this.kulcsTorles || undefined,
      }));
      this.kulcs = '';
      this.kulcsTorles = false;
      this.atvesz(r);
      this.data.sendToast('Mentve. Az új beállítás azonnal érvényes.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.ment = false;
    }
  }

  /** A képernyőn álló (még nem mentett) adatokkal próbál; üres kulcsnál a tárolttal. */
  async kapcsolatProba() {
    if (this.probaFut) return;
    this.probaFut = true;
    this.proba = null;
    try {
      this.proba = await firstValueFrom(this.http.post('/api/hibajegy/beallitas/proba', {
        redmine_url: this.b.redmine_url, projekt: this.b.projekt, api_kulcs: this.kulcs || undefined,
      }));
    } catch (e: any) {
      this.proba = { ok: false, hibak: [e?.error?.error || 'A próba nem sikerült.'] };
    } finally {
      this.probaFut = false;
    }
  }

  async szinkronMost() {
    if (this.szinkronFut) return;
    this.szinkronFut = true;
    this.szinkron = null;
    try {
      this.szinkron = await firstValueFrom(this.http.post('/api/hibajegy/beallitas/szinkron', {}));
      await this.betolt();
    } catch (e: any) {
      this.szinkron = { hiba: e?.error?.error || 'A szinkron nem sikerült.' };
    } finally {
      this.szinkronFut = false;
    }
  }

  formatumNev(f: string): string {
    return f === 'textile' ? 'Textile' : 'Markdown (CommonMark)';
  }
}
